import { useMemo, useState } from 'react'
import { Lock, Plus, Trash2 } from 'lucide-react'
import {
  type StructureData, type Group, type User, type GroupType,
  GROUP_TYPE_LABELS, fullGroupName, inheritedGroupSources,
} from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { useGlobalStandardGroups, isGlobalGroupId } from '../useGlobalStandardGroups.ts'
import { useMatrixCaps } from './matrix/MatrixCapabilities.tsx'
import {
  Button,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  MatrixTable,
  Select,
  type MatrixColumn,
  type MatrixColumnGroup,
  type MatrixRow,
} from '@/components/ui'
import { GROUP_TYPE_HEADER_CLASS } from './matrix/typeColors.ts'
import { cn } from '@/lib/utils.ts'

/**
 * User × Gruppen-Matrix mit Tag-Filter pro Gruppentyp. Nutzt die zentrale
 * `MatrixTable`-Komponente (identischer Look zur Zugriffsmatrix).
 * Globale Standard-Gruppen werden links eingeblendet und sind read-only.
 */

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
  groupTypes: GroupType[]
  activeTypes: GroupType[]
  onActiveTypesChange: (next: GroupType[]) => void
}

interface UserRow extends MatrixRow {
  user: User
}

interface GroupCol extends MatrixColumn {
  group: Group
  isGlobal: boolean
}

export function UserGroupMatrix({
  data, update, groupTypes, activeTypes, onActiveTypesChange,
}: Props) {
  const a = useAnonymize()
  const caps = useMatrixCaps()
  const creatableTypes = useMemo<GroupType[]>(
    () => groupTypes.filter((t) => caps.canCreateGroupTypes.includes(t)),
    [groupTypes, caps.canCreateGroupTypes],
  )
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupType, setNewGroupType] = useState<GroupType>(
    creatableTypes[0] ?? activeTypes[0] ?? groupTypes[0],
  )
  const [newUsername, setNewUsername] = useState('')

  const globalGroups = useGlobalStandardGroups()
  const visibleTypes = useMemo(
    () => groupTypes.filter((t) => activeTypes.includes(t)),
    [groupTypes, activeTypes],
  )
  const allGroups = useMemo<Group[]>(
    () => [...globalGroups, ...data.groups],
    [globalGroups, data.groups],
  )
  const groupsByType = useMemo(() => {
    const map = new Map<GroupType, Group[]>()
    for (const t of groupTypes) map.set(t, allGroups.filter((g) => g.type === t))
    return map
  }, [allGroups, groupTypes])

  const newGroupTypeOptions = useMemo(
    () => creatableTypes.map((t) => ({ value: t, label: `${t} — ${GROUP_TYPE_LABELS[t]}` })),
    [creatableTypes],
  )
  const canAddGroups = creatableTypes.length > 0
  const showTypeSelect = creatableTypes.length > 1

  function toggleType(t: GroupType) {
    if (activeTypes.includes(t)) {
      onActiveTypesChange(groupTypes.filter((g) => activeTypes.includes(g) && g !== t))
    } else {
      onActiveTypesChange(groupTypes.filter((g) => activeTypes.includes(g) || g === t))
    }
  }

  function addGroup() {
    const name = newGroupName.trim()
    if (!name) return
    const g: Group = { id: crypto.randomUUID(), type: newGroupType, name, scope: 'global' }
    update({ groups: [...data.groups, g] })
    setNewGroupName('')
    if (!activeTypes.includes(newGroupType)) {
      onActiveTypesChange(groupTypes.filter((t) => activeTypes.includes(t) || t === newGroupType))
    }
  }

  function renameGroup(id: string, name: string) {
    if (isGlobalGroupId(id)) return
    update({ groups: data.groups.map((g) => (g.id === id ? { ...g, name } : g)) })
  }

  function deleteGroup(id: string) {
    if (isGlobalGroupId(id)) return
    update({
      groups: data.groups
        .filter((g) => g.id !== id)
        .map((g) => {
          if (!g.memberOfGroupIds?.includes(id)) return g
          const next = g.memberOfGroupIds.filter((gid) => gid !== id)
          return { ...g, memberOfGroupIds: next.length > 0 ? next : undefined }
        }),
      users: data.users.map((u) => ({ ...u, groupIds: u.groupIds.filter((gid) => gid !== id) })),
      permissions: data.permissions.filter((p) => p.groupId !== id),
    })
  }

  function addUser() {
    const username = newUsername.trim()
    if (!username) return
    const u: User = { id: crypto.randomUUID(), username, groupIds: [] }
    update({ users: [...data.users, u] })
    setNewUsername('')
  }

  function deleteUser(id: string) {
    update({ users: data.users.filter((u) => u.id !== id) })
  }

  function toggleGroupForUser(userId: string, groupId: string) {
    const u = data.users.find((x) => x.id === userId)
    if (!u) return
    const groupIds = u.groupIds.includes(groupId)
      ? u.groupIds.filter((g) => g !== groupId)
      : [...u.groupIds, groupId]
    update({ users: data.users.map((x) => (x.id === userId ? { ...x, groupIds } : x)) })
  }

  function toggleAllForGroup(groupId: string) {
    const someoneMissing = data.users.some((u) => !u.groupIds.includes(groupId))
    update({
      users: data.users.map((u) => ({
        ...u,
        groupIds: someoneMissing
          ? (u.groupIds.includes(groupId) ? u.groupIds : [...u.groupIds, groupId])
          : u.groupIds.filter((g) => g !== groupId),
      })),
    })
  }

  // ─── MatrixTable-Props ──────────────────────────────────────────────
  const columnGroups = useMemo<MatrixColumnGroup<GroupCol>[]>(() => {
    return visibleTypes
      .filter((t) => (groupsByType.get(t)?.length ?? 0) > 0)
      .map((t) => {
        const grps = groupsByType.get(t) ?? []
        return {
          id: t,
          label: (
            <span>
              <span className="font-mono mr-1">{t}</span>
              {GROUP_TYPE_LABELS[t]}
              <span className="ml-1.5 text-caption opacity-70">({grps.length})</span>
            </span>
          ),
          headerClass: GROUP_TYPE_HEADER_CLASS[t],
          columns: grps.map<GroupCol>((g) => {
            const isGlobal = isGlobalGroupId(g.id)
            return {
              id: g.id,
              group: g,
              isGlobal,
              label: (
                <span className={cn('flex items-center gap-0.5', isGlobal && 'text-gray-500 italic')}>
                  {isGlobal && <Lock className="w-2.5 h-2.5 flex-shrink-0" />}
                  {a.token(g.teamName || g.name)}
                </span>
              ),
              title: isGlobal
                ? `Globale Standard-Gruppe — ${g.name}${g.description ? ` · ${g.description}` : ''}`
                : `${fullGroupName(data.domainPrefix, g.type, g.name)} — Doppelklick zum Umbenennen`,
              widthPx: 32,
              headerFooter: (
                <>
                  <button
                    type="button"
                    onClick={() => toggleAllForGroup(g.id)}
                    className="text-[9px] text-gray-500 hover:text-brand-600 underline leading-none"
                    title="Alle User togglen"
                  >
                    alle
                  </button>
                  {isGlobal ? (
                    <span className="text-gray-300" title="Globale Standard-Gruppe — Bearbeitung unter Globale Standards">
                      <Lock className="w-3 h-3" />
                    </span>
                  ) : (
                    <IconButton
                      icon={<Trash2 className="w-3 h-3" />}
                      aria-label="Gruppe löschen"
                      variant="ghost"
                      size="xs"
                      onClick={() => deleteGroup(g.id)}
                      className="text-gray-400 hover:text-red-500 p-0 h-auto min-h-0"
                      title="Gruppe löschen"
                    />
                  )}
                </>
              ),
            }
          }),
        }
      })
  }, [visibleTypes, groupsByType, a, data.domainPrefix]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo<UserRow[]>(
    () =>
      data.users.map((u) => ({
        id: u.id,
        user: u,
        label: (
          <div className="flex items-center gap-2 font-mono text-caption">
            <span>{a.token(u.username)}</span>
            <IconButton
              icon={<Trash2 className="w-3 h-3" />}
              aria-label="User löschen"
              variant="ghost"
              size="xs"
              onClick={() => deleteUser(u.id)}
              className="ml-auto text-gray-300 hover:text-red-500 p-0 h-auto min-h-0"
              title="User löschen"
            />
          </div>
        ),
      })),
    [data.users, a], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const renderCell = (r: UserRow, c: GroupCol): ReactCellType => {
    const u = r.user
    const g = c.group
    const inherited = inheritedGroupSources(u, allGroups)
    const direct = u.groupIds.includes(g.id)
    const inheritedFrom = inherited.get(g.id)
    const viaInheritance = !direct && !!inheritedFrom && inheritedFrom.size > 0
    const bothDirectAndInherited = direct && !!inheritedFrom && inheritedFrom.size > 0
    const bg = direct
      ? 'bg-emerald-100'
      : viaInheritance
        ? 'bg-emerald-50'
        : 'bg-white'
    const textClass = direct
      ? 'text-emerald-700 font-semibold'
      : viaInheritance
        ? 'text-emerald-500'
        : 'text-gray-300'
    const sourceNames = inheritedFrom
      ? [...inheritedFrom]
          .map((gid) => allGroups.find((gg) => gg.id === gid))
          .filter((gg): gg is Group => !!gg)
          .map((gg) => gg.teamName || gg.name)
          .join(', ')
      : ''
    const title = direct
      ? bothDirectAndInherited
        ? `Direkt-Mitglied (zusaetzlich geerbt ueber: ${sourceNames}) — Klick entfernt nur die Direkt-Mitgliedschaft`
        : 'Direkt-Mitglied — Klick zum Entfernen'
      : viaInheritance
        ? `Geerbt ueber: ${sourceNames} (AD-Nesting) — in der Abteilungs-Matrix bearbeitbar`
        : 'Nicht Mitglied — Klick zum Hinzufuegen'
    return (
      <button
        type="button"
        onClick={() => toggleGroupForUser(u.id, g.id)}
        onDoubleClick={() => {
          if (c.isGlobal || a.enabled) return
          const next = window.prompt('Gruppe umbenennen:', g.name)
          if (next && next.trim() && next.trim() !== g.name) renameGroup(g.id, next.trim())
        }}
        title={title}
        className={cn(
          'w-full h-full text-caption font-mono px-1 py-1 border-0 select-none cursor-pointer',
          'hover:ring-1 hover:ring-inset hover:ring-brand-500 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-500',
          bg,
          textClass,
        )}
      >
        {direct ? '✓' : viaInheritance ? '✓' : '·'}
      </button>
    )
  }

  return (
    <div className="space-y-3">
      <FilterBar>
        <span className="text-caption font-medium text-gray-600 mr-1">Anzeigen:</span>
        {groupTypes.map((t) => {
          const active = activeTypes.includes(t)
          const count = groupsByType.get(t)?.length ?? 0
          return (
            <FilterChip
              key={t}
              active={active}
              count={count}
              onClick={() => toggleType(t)}
              onRemove={active ? () => toggleType(t) : undefined}
              title={active ? `${GROUP_TYPE_LABELS[t]} ausblenden` : `${GROUP_TYPE_LABELS[t]} einblenden`}
            >
              <span className="font-mono mr-1">{t}</span>
              <span>{GROUP_TYPE_LABELS[t]}</span>
            </FilterChip>
          )
        })}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {canAddGroups && (
            <>
              {showTypeSelect && (
                <div className="w-56">
                  <Select
                    value={newGroupType}
                    onValueChange={(v) => setNewGroupType(v as GroupType)}
                    options={newGroupTypeOptions}
                    triggerClassName="py-1.5 text-small"
                  />
                </div>
              )}
              <Input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addGroup()}
                placeholder={`Neue ${GROUP_TYPE_LABELS[newGroupType]}-Gruppe`}
                wrapperClassName="w-56"
                className="py-1.5 text-small"
              />
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-3.5 h-3.5" />} onClick={addGroup}>
                Gruppe
              </Button>
            </>
          )}
          <Input
            type="text"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addUser()}
            placeholder="Neuer User (sAMAccountName)"
            wrapperClassName="w-56"
            className="py-1.5 text-small"
          />
          <Button variant="primary" size="sm" leadingIcon={<Plus className="w-3.5 h-3.5" />} onClick={addUser}>
            User
          </Button>
        </div>
      </FilterBar>

      <MatrixTable<UserRow, GroupCol>
        rows={rows}
        labelColumns={[
          {
            id: 'user',
            header: 'User',
            widthPx: 220,
            render: (r) => r.label,
          },
        ]}
        columnGroups={columnGroups}
        renderCell={renderCell}
        verticalHeaderHeight={80}
      />
    </div>
  )
}

type ReactCellType = ReturnType<typeof Object>
